import { Alert, Button, Stack, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { AuthLayout } from "../accounts_login_ui/AuthLayout";
import { api, ensureCsrf, errorMessage, setActiveOrganization } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { LoadingState } from "../components";

export function AcceptInvitation() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { me, refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const info = useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/accounts/invitations/accept/{token}/", { params: { path: { token } } });
      if (error) throw new Error(errorMessage(error));
      return data!;
    },
  });

  async function accept(e: FormEvent) {
    e.preventDefault();
    setError(null);
    await ensureCsrf();
    const body = info.data?.user_exists || me ? {} : { password, first_name: firstName, last_name: lastName };
    const { data, error: err } = await api.POST("/api/v1/accounts/invitations/accept/{token}/", { params: { path: { token } }, body });
    if (err || !data) { setError(errorMessage(err)); return; }
    setActiveOrganization(String(data.organization));
    await refresh();
    navigate("/app", { replace: true });
  }

  if (info.isLoading) return <AuthLayout title="Invitation"><LoadingState /></AuthLayout>;
  if (info.isError || !info.data) return <AuthLayout title="Invitation"><Alert severity="error">This invitation link is not valid.</Alert></AuthLayout>;
  const d = info.data;
  const wrongUser = me && me.email.toLowerCase() !== d.email.toLowerCase();
  return (
    <AuthLayout title={`Join ${d.organization_name}`} subtitle={`${d.invited_by || "A colleague"} invited ${d.email}.`}>
      {!d.is_open ? <Alert severity="warning">This invitation has {d.status === "accepted" ? "already been accepted" : "expired or been revoked"}. Ask for a new one.</Alert> : wrongUser ? (
        <Alert severity="warning">You are signed in as {me?.email}. Sign out and open this link again with {d.email}.</Alert>
      ) : (
        <form onSubmit={accept}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {!me && d.user_exists && <Alert severity="info">You already have an account. <RouterLink to={`/login?next=/invitations/${token}`}>Sign in</RouterLink> first, then open this link again.</Alert>}
            {!me && !d.user_exists && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
                  <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Stack>
                <TextField label="Choose a password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
              </>
            )}
            {(me || !d.user_exists) && <Button type="submit" variant="contained" size="large">Accept invitation</Button>}
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}
